// Again PDP — variant resolution, colour-filtered gallery, multi-buy picker (1 / 2 / 3 pairs, each pair its
// own colour + size, priced with the store's automatic-discount math), ajax add-to-cart of one 1-pair
// variant per pair through Horizon's CartLinesUpdateEvent contract (drawer auto-opens and morphs in place),
// size-guide drawer, sticky mobile ATC and the delivery estimate.
import { CartLinesUpdateEvent } from '@shopify/events';

function money(cents, format) {
  const f = format || '${{amount}}';
  const key = (f.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
  const n = cents / 100;
  const delim = (num, dec, th, dp) => {
    const parts = num.toFixed(dec).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
    return parts.join(dp);
  };
  let s;
  switch (key) {
    case 'amount_no_decimals': s = delim(n, 0, ',', '.'); break;
    case 'amount_with_comma_separator': s = delim(n, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': s = delim(n, 0, '.', ','); break;
    default: s = delim(n, 2, ',', '.');
  }
  return f.replace(/\{\{\s*\w+\s*\}\}/, s);
}

const handleize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

class AgainProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.cpos = Number(this.dataset.colorPos) || 0;
    this.spos = Number(this.dataset.sizePos) || 0;
    this.bundle = this.dataset.bundle === 'true';
    this.pairs = this.bundle ? Math.min(3, Math.max(1, Number(this.dataset.pairs) || 1)) : 1;
    this.threeOff = Number(this.dataset.threeOff) || 0;
    // Shopify converts D2's fixed amount at checkout with the live rate; match it exactly when we have it
    const cur = window.Shopify && window.Shopify.currency;
    if (cur && cur.active && cur.active !== this.dataset.shopCurrency && Number(cur.rate) > 0) {
      this.threeOff = Math.round((Number(this.dataset.threeOffBase) || 0) * Number(cur.rate));
    }
    this.touched = {}; // pair rows she changed herself stop copying pair 1
    this.sizeChosen = this.dataset.sizeChosen === 'true';
    this.fmt = this.dataset.moneyFormat;
    this.form = this.querySelector('[data-product-form]');
    this.input = this.querySelector('[data-variant-input]');
    this.current = this.variants.find((v) => String(v.id) === this.input.value) || this.variants[0];

    this.form.addEventListener('change', (e) => this.onChange(e));
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add();
    });

    this.setupGallery();
    this.setupSizeGuide();
    this.setupSticky();
    this.setupDyn();
    this.renderDelivery();
    this.renderAvailability();
    if (this.bundle) {
      this.syncPairs();
      this.renderBundle();
    }

    // card colour dots link here with ?color=<value>
    const qc = new URL(window.location.href).searchParams.get('color');
    if (qc && this.cpos) {
      const r = [...this.form.querySelectorAll('input[name="ag-color"]')].find((i) => i.value === qc);
      if (r && !r.checked) {
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* ---------------- variants ---------------- */
  selectedOptions() {
    const opts = this.current.options.slice();
    this.form.querySelectorAll('fieldset[data-option]').forEach((fs) => {
      const checked = fs.querySelector('input:checked');
      if (checked) opts[Number(fs.dataset.option) - 1] = checked.value;
    });
    return opts;
  }

  find(opts) {
    return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
  }

  onChange(e) {
    const name = e.target.name || '';
    if (!name.startsWith('ag-')) return;
    if (name === 'ag-pairs') return this.setPairs(Number(e.target.value) || 1);
    const pm = name.match(/^ag-p(\d)-(color|size)$/);
    if (pm) return this.onPairChange(Number(pm[1]), e.target);
    if (name === 'ag-size') {
      this.sizeChosen = true;
      this.querySelector('[data-size-field]')?.classList.remove('is-missing');
      const err = this.querySelector('[data-size-error]');
      if (err) err.hidden = true;
    }
    const opts = this.selectedOptions();
    let v = this.find(opts);
    if (!v) {
      // keep the colour, fall back to any size
      v = this.variants.find((x) =>
        x.options.every((o, i) => i === this.spos - 1 || o === opts[i])
      );
    }
    if (!v) return;
    this.current = v;
    this.input.value = v.id;
    this.render(name === 'ag-color');
  }

  variantFor(overrides) {
    const opts = this.current.options.slice();
    Object.entries(overrides).forEach(([pos, val]) => (opts[Number(pos) - 1] = val));
    return this.find(opts);
  }

  render(colourChanged) {
    const v = this.current;
    const $ = (s) => this.querySelector(s);

    // price block (Again shoes: renderBundle prices the whole pick)
    if (!this.bundle) {
      $('[data-price]').textContent = money(v.price, this.fmt);
      const hasCompare = v.compare > v.price;
      $('[data-compare]').hidden = !hasCompare;
      if (hasCompare) $('[data-compare]').textContent = money(v.compare, this.fmt);
      const pct = hasCompare ? Math.round(((v.compare - v.price) * 100) / v.compare) : 0;
      $('[data-save]').hidden = !pct;
      $('[data-save-text]').textContent = `Save ${pct}%`;
    }

    // option labels
    this.form.querySelectorAll('fieldset[data-option]').forEach((fs) => {
      const label = fs.querySelector('[data-label]');
      const pos = Number(fs.dataset.option);
      if (!label) return;
      if (pos === this.spos && !this.sizeChosen) label.textContent = 'Select';
      else label.textContent = v.options[pos - 1];
    });

    // tier card pictures follow pair 1's colour
    if (this.bundle && v.img) {
      this.querySelectorAll('[data-tier] img').forEach((img) => {
        img.src = v.img;
        img.removeAttribute('srcset');
      });
    }
    if (this.bundle) {
      this.syncPairs();
      this.renderBundle();
    }
    const inst = this.querySelector('[data-installments-id]');
    if (inst && inst.value !== String(v.id)) {
      inst.value = v.id;
      inst.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // ATC + sticky
    this.renderAtc();
    const stickyImg = this.querySelector('[data-sticky-img]');
    if (stickyImg && v.img) {
      stickyImg.src = v.img;
      stickyImg.removeAttribute('srcset');
    }

    this.renderAvailability();
    if (colourChanged && this.cpos) this.applyColour(handleize(v.options[this.cpos - 1]), v.media);

    if (this.sizeChosen) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  renderAtc() {
    const v = this.current;
    const btn = this.querySelector('[data-atc]');
    const label = this.querySelector('[data-atc-label]');
    const picks = this.bundle ? this.picks() : [v];
    const soldOut = picks.some((p) => p && !p.available);
    btn.disabled = soldOut;
    label.textContent = soldOut ? 'Sold out' : this.sizeChosen ? 'Add to cart' : 'Select your size';
    const t = this.totals();
    this.querySelector('[data-atc-price]').textContent = money(t.total, this.fmt);
    const c = this.querySelector('[data-atc-compare]');
    c.hidden = !(t.list > t.total);
    if (!c.hidden) c.textContent = money(t.list, this.fmt);

    const sBtn = this.querySelector('[data-sticky-btn]');
    // sticky button names the pair count ("Add 2 pairs", copy deck §2.5); one pair / other products: "Add to cart"
    const addLabel = this.bundle && this.pairs > 1 ? `Add ${this.pairs} pairs` : 'Add to cart';
    if (sBtn) sBtn.textContent = soldOut ? 'Sold out' : this.sizeChosen ? addLabel : 'Choose size';
    const sPrice = this.querySelector('[data-sticky-price]');
    if (sPrice) sPrice.textContent = money(t.total, this.fmt);
    const sCmp = this.querySelector('[data-sticky-compare]');
    if (sCmp) {
      sCmp.hidden = !(t.list > t.total);
      if (!sCmp.hidden) sCmp.textContent = money(t.list, this.fmt);
    }
    const sMeta = this.querySelector('[data-sticky-meta]');
    if (sMeta && this.bundle) sMeta.textContent = `${this.pairs === 1 ? '1 pair' : `${this.pairs} pairs`} · `;
    const dyn = this.querySelector('[data-dyn]');
    if (dyn) {
      dyn.toggleAttribute('data-locked', !this.sizeChosen);
      // "Buy it now" buys the one variant in the form: only offered for a single pair
      dyn.hidden = this.bundle && this.pairs > 1;
    }
  }

  /* ---------------- multi-buy: pairs ---------------- */
  // the store's automatic discounts, same as snippet again-bundle: every second pair free (cheapest free),
  // or threeOff off 3+ pairs; they don't combine, so the bigger saving applies
  bundleTotal(prices) {
    const list = prices.reduce((a, b) => a + b, 0);
    if (!this.bundle || prices.length < 2) return { list, total: list, off: 0 };
    const asc = prices.slice().sort((a, b) => a - b);
    const d1 = asc.slice(0, Math.floor(prices.length / 2)).reduce((a, b) => a + b, 0);
    const d2 = prices.length >= 3 ? Math.min(this.threeOff, list) : 0;
    const off = Math.max(d1, d2);
    return { list, total: list - off, off };
  }

  pairRow(i) {
    return this.querySelector(`[data-pair="${i}"]`);
  }

  // the variant a pair row points at (null while its size is unset)
  pairVariant(i) {
    if (i === 1) return this.current;
    const row = this.pairRow(i);
    if (!row) return null;
    const opts = this.current.options.slice();
    const col = row.querySelector(`input[name="ag-p${i}-color"]:checked`);
    const size = row.querySelector('[data-pair-size]');
    if (this.cpos && col) opts[this.cpos - 1] = col.value;
    if (this.spos) {
      if (!size || !size.value) return null;
      opts[this.spos - 1] = size.value;
    }
    return this.find(opts);
  }

  picks() {
    const out = [];
    for (let i = 1; i <= this.pairs; i += 1) out.push(this.pairVariant(i));
    return out;
  }

  // totals for the current pick; a pair without a size yet is priced like pair 1
  totals(n = this.pairs) {
    const prices = [];
    const compares = [];
    for (let i = 1; i <= n; i += 1) {
      const v = this.pairVariant(i) || this.current;
      prices.push(v.price);
      compares.push(v.compare > v.price ? v.compare : v.price);
    }
    const t = this.bundleTotal(prices);
    const cmp = compares.reduce((a, b) => a + b, 0);
    return { total: t.total, list: Math.max(cmp, t.list), off: t.off };
  }

  setPairs(n) {
    this.pairs = Math.min(3, Math.max(1, n));
    this.syncPairs();
    this.renderBundle();
    this.renderAtc();
  }

  onPairChange(i, el) {
    this.touched[i] = true;
    const row = this.pairRow(i);
    if (el.matches('[data-pair-size]') && el.value) {
      row?.classList.remove('is-missing');
      const err = row?.querySelector('[data-pair-error]');
      if (err) err.hidden = true;
    }
    this.renderBundle();
    this.renderAtc();
  }

  // show / enable the rows up to the pair count; untouched rows copy pair 1's colour and size
  syncPairs() {
    const v = this.current;
    const head = this.querySelector('[data-pair1-head]');
    if (head) head.hidden = this.pairs < 2;
    this.querySelectorAll('[data-pair]').forEach((row) => {
      const i = Number(row.dataset.pair);
      const on = i <= this.pairs;
      row.hidden = !on;
      row.disabled = !on;
      if (!this.touched[i]) {
        if (this.cpos) {
          const r = [...row.querySelectorAll(`input[name="ag-p${i}-color"]`)].find((x) => x.value === v.options[this.cpos - 1]);
          if (r) r.checked = true;
        }
        const size = row.querySelector('[data-pair-size]');
        if (size && this.spos && this.sizeChosen) size.value = v.options[this.spos - 1];
      }
    });
    const radio = this.querySelector(`input[name="ag-pairs"][value="${this.pairs}"]`);
    if (radio && !radio.checked) radio.checked = true;
  }

  renderBundle() {
    if (!this.bundle) return;
    const $ = (s) => this.querySelector(s);
    const t = this.totals();
    const n = this.pairs;
    $('[data-price]').textContent = money(t.total, this.fmt);
    const unit = $('[data-price-unit]');
    if (unit) unit.textContent = n > 1 ? `for ${n} pairs` : 'for 1 pair';
    const cmp = $('[data-compare]');
    cmp.hidden = !(t.list > t.total);
    if (!cmp.hidden) cmp.textContent = money(t.list, this.fmt);
    const pct = t.list > t.total ? Math.round(((t.list - t.total) * 100) / t.list) : 0;
    $('[data-save]').hidden = !pct;
    $('[data-save-text]').textContent = `Save ${pct}%`;

    // tier cards: what 1 / 2 / 3 pairs of the current pick cost
    const tt = {};
    this.querySelectorAll('[data-tier]').forEach((tier) => {
      const k = Number(tier.dataset.pairs) || 1;
      const x = this.totals(k);
      tt[k] = x;
      tier.querySelector('[data-tier-price]').textContent = money(x.total, this.fmt);
      tier.querySelector('[data-tier-per]').textContent = money(Math.round(x.total / k), this.fmt);
      const c = tier.querySelector('[data-tier-compare]');
      c.hidden = !(x.list > x.total);
      if (!c.hidden) c.textContent = money(x.list, this.fmt);
      const save = tier.querySelector('[data-tier-save]');
      const tp = x.list > x.total ? Math.round(((x.list - x.total) * 100) / x.list) : 0;
      save.textContent = tp ? `Save ${tp}%` : 'Single pair';
      save.classList.toggle('ag-tier__save--none', !tp);
    });
    const free = tt[1] && tt[2] && tt[2].total === tt[1].total;
    const freeEl = $('[data-tier-free]');
    if (freeEl) freeEl.hidden = !free;
    const add3 = $('[data-pair3-add]');
    if (add3 && tt[3] && tt[2]) add3.textContent = `+${money(tt[3].total - tt[2].total, this.fmt)}`;

    // offer bar
    const el = $('[data-offer-math]');
    const m = (x) => `<strong>${money(x, this.fmt)}</strong>`;
    if (el && tt[2]) {
      if (n === 1) el.innerHTML = free ? `Add a second pair free: 2 pairs are ${m(tt[2].total)}` : `2 pairs ${m(tt[2].total)}`;
      else if (n === 2) el.innerHTML = `2 pairs ${m(t.total)}${free ? ', second pair free' : ''}`;
      else el.innerHTML = `${n} pairs ${m(t.total)}`;
    }

    // pair summaries ("Black · size 9")
    this.querySelectorAll('[data-pair]').forEach((row) => {
      const i = Number(row.dataset.pair);
      const sum = row.querySelector('[data-pair-sum]');
      const col = row.querySelector(`input[name="ag-p${i}-color"]:checked`)?.value || '';
      const size = row.querySelector('[data-pair-size]')?.value || '';
      if (sum) sum.textContent = [col, size ? `size ${size}` : 'pick a size'].filter(Boolean).join(' · ');
      const sel = row.querySelector('[data-pair-size]');
      if (sel && this.spos) {
        [...sel.options].forEach((o) => {
          if (!o.value) return;
          const opts = this.current.options.slice();
          if (this.cpos && col) opts[this.cpos - 1] = col;
          opts[this.spos - 1] = o.value;
          const pv = this.find(opts);
          o.disabled = !pv || !pv.available;
        });
      }
    });

    this.renderBox();
    const live = $('[data-bundle-live]');
    if (live) live.textContent = `${n === 1 ? '1 pair' : `${n} pairs`}, ${money(t.total, this.fmt)} in total${n === 2 && free ? ', second pair free' : ''}.`;
  }

  // "In your order: 2 pairs of Again Shift (Black 8, Purple 9) + the free guide (PDF)"
  renderBox() {
    const box = this.querySelector('[data-box]');
    const text = box?.querySelector('[data-box-text]');
    if (!text || !this.bundle) return;
    const n = this.pairs;
    const esc = (x) => String(x).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]);
    const parts = [];
    for (let i = 1; i <= n; i += 1) {
      const v = this.pairVariant(i);
      if (!v || (i === 1 && !this.sizeChosen)) continue;
      parts.push([this.cpos ? v.options[this.cpos - 1] : '', this.spos ? v.options[this.spos - 1] : ''].filter(Boolean).join(' '));
    }
    const detail = n > 1 && parts.length === n ? ` (${esc(parts.join(', '))})` : '';
    const gift = box.dataset.gift === 'true' ? ' + <strong>the free guide</strong> (PDF)' : '';
    text.innerHTML = `In your order: <strong>${n} ${n === 1 ? 'pair' : 'pairs'}</strong> of ${esc(box.dataset.short || '')}${detail}${gift}`;
  }

  renderAvailability() {
    if (!this.spos) return;
    this.form.querySelectorAll('input[name="ag-size"]').forEach((input) => {
      const v = this.variantFor({ [this.spos]: input.value });
      input.closest('.ag-size')?.classList.toggle('is-unavailable', !v || !v.available);
    });
  }

  promptSize() {
    const field = this.querySelector('[data-size-field]');
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.classList.remove('is-missing');
    void field.offsetWidth;
    field.classList.add('is-missing');
    const err = this.querySelector('[data-size-error]');
    if (err) err.hidden = false;
  }

  /* ---------------- add to cart ---------------- */
  // every visible pair needs a size; the first row missing one is flagged and focused
  checkPairs() {
    for (let i = 2; i <= this.pairs; i += 1) {
      const row = this.pairRow(i);
      const sel = row?.querySelector('[data-pair-size]');
      if (sel && !sel.value) {
        row.classList.remove('is-missing');
        void row.offsetWidth;
        row.classList.add('is-missing');
        const err = row.querySelector('[data-pair-error]');
        if (err) err.hidden = false;
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sel.focus({ preventScroll: true });
        return false;
      }
    }
    return true;
  }

  async add() {
    if (!this.sizeChosen) return this.promptSize();
    if (this.bundle && !this.checkPairs()) return;
    const btn = this.querySelector('[data-atc]');
    const errEl = this.querySelector('[data-atc-error]');
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    errEl.hidden = true;

    // one 1-pair variant per pair; identical picks merge into one line
    const qty = new Map();
    (this.bundle ? this.picks() : [this.current]).forEach((v) => {
      if (v) qty.set(v.id, (qty.get(v.id) || 0) + 1);
    });
    const items = [...qty].map(([id, quantity]) => ({ id, quantity }));
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await res.json();
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: added.sections,
          source: 'again-product',
          didError: Boolean(added.status),
        },
      });
      if (added.status) {
        errEl.textContent = added.description || added.message || 'Could not add to cart.';
        errEl.hidden = false;
      }
    } catch (error) {
      deferred.reject(error);
      errEl.textContent = 'Something went wrong. Please try again.';
      errEl.hidden = false;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  setupDyn() {
    const dyn = this.querySelector('[data-dyn]');
    if (!dyn) return;
    dyn.addEventListener(
      'click',
      (e) => {
        if (!dyn.hasAttribute('data-locked')) return;
        e.preventDefault();
        e.stopPropagation();
        this.promptSize();
      },
      true
    );
  }

  /* ---------------- gallery ---------------- */
  setupGallery() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.thumbs.forEach((t) =>
      t.addEventListener('click', () => {
        if (this.slides.some((s) => s.dataset.mediaId === t.dataset.mediaId && s.classList.contains('is-more'))) this.expandGrid();
        const list = this.visibleSlides();
        const i = list.findIndex((s) => s.dataset.mediaId === t.dataset.mediaId);
        if (i >= 0) this.goTo(i);
      })
    );
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.syncIndex());
    });
    this.index = 0;
    this.syncIndex();
    this.querySelector('[data-more]')?.addEventListener('click', () => this.expandGrid());
    this.capGrid();
    // desktop grid: the rail follows whichever image is in view
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          if (!this.isGrid()) return;
          const hit = entries.find((e) => e.isIntersecting);
          if (!hit) return;
          const list = this.visibleSlides();
          const i = list.indexOf(hit.target);
          if (i >= 0) {
            this.index = i;
            this.markActive(list);
          }
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      this.slides.forEach((s) => this.io.observe(s));
    }
  }

  // desktop grid shows the first GRID_CAP images (about the buy box's height); the rest wait behind
  // "Show all N photos". The mobile slider and the thumbnail rail always have every image.
  capGrid() {
    const cap = 9;
    const gallery = this.querySelector('[data-gallery]');
    const btn = this.querySelector('[data-more]');
    if (!gallery || !btn) return;
    const list = this.visibleSlides();
    this.slides.forEach((s) => s.classList.remove('is-more'));
    list.forEach((s, i) => s.classList.toggle('is-more', i >= cap));
    const extra = list.length > cap && !gallery.classList.contains('is-expanded');
    btn.hidden = !extra;
    btn.textContent = `Show all ${list.length} photos`;
  }

  expandGrid() {
    this.querySelector('[data-gallery]')?.classList.add('is-expanded');
    const btn = this.querySelector('[data-more]');
    if (btn) btn.hidden = true;
  }

  visibleSlides() {
    return this.slides
      .filter((s) => !s.hidden)
      .sort((a, b) => Number(a.style.order) - Number(b.style.order));
  }

  isGrid() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  goTo(i, instant) {
    const list = this.visibleSlides();
    if (!list.length) return;
    const n = (i + list.length) % list.length;
    if (this.isGrid()) list[n].scrollIntoView({ behavior: instant ? 'instant' : 'smooth', block: 'start' });
    else this.track.scrollTo({ left: list[n].offsetLeft, behavior: instant ? 'instant' : 'smooth' });
    this.index = n;
    this.markActive(list);
  }

  syncIndex() {
    const list = this.visibleSlides();
    const x = this.track.scrollLeft;
    let best = 0;
    let dist = Infinity;
    list.forEach((s, i) => {
      const d = Math.abs(s.offsetLeft - x);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    this.index = best;
    this.markActive(list);
  }

  markActive(list) {
    const active = list[this.index];
    this.thumbs.forEach((t) => t.classList.toggle('is-active', active && t.dataset.mediaId === active.dataset.mediaId));
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = list.length > 1 ? `${this.index + 1} / ${list.length}` : '';
    const at = this.thumbs.find((t) => t.classList.contains('is-active'));
    const rail = this.querySelector('[data-thumbs]');
    if (at && rail) {
      const r = rail.getBoundingClientRect();
      const a = at.getBoundingClientRect();
      if (a.top < r.top || a.bottom > r.bottom || a.left < r.left || a.right > r.right) {
        rail.scrollBy({ top: a.top - r.top - 8, left: a.left - r.left - 8, behavior: 'smooth' });
      }
    }
  }

  // Same bands as the Liquid pass in again-product.liquid (keep the two in step):
  // 0 lead packshot · 1000 selected-colour callouts · 2000 one lifestyle photo · 3000 other
  // explaining images · 4000 the colour's other packshots · 5000 colourless packshots ·
  // 6000 selected-colour lifestyle · 7000 other lifestyle; other colours' packshots/callouts hidden.
  applyColour(handle) {
    if (!this.slides) return;
    const hasIg = this.querySelector('[data-gallery]')?.dataset.hasIg === 'true';
    const byIndex = [...this.slides].sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
    let lead = -1;
    let lifeSel = -1;
    let lifeAny = -1;
    byIndex.forEach((el) => {
      if (el.dataset.ig) return;
      const i = Number(el.dataset.index);
      const c = el.dataset.colour || '';
      if (el.dataset.kind === 'pack') {
        if (lead === -1 && c && c === handle) lead = i;
      } else if (hasIg) {
        if (lifeSel === -1 && c === handle) lifeSel = i;
        if (lifeAny === -1) lifeAny = i;
      }
    });
    const life = lifeSel !== -1 ? lifeSel : lifeAny;
    const apply = (el) => {
      const i = Number(el.dataset.index);
      const c = el.dataset.colour || '';
      const ig = el.dataset.ig || '';
      const pack = el.dataset.kind === 'pack';
      let hidden = false;
      let ord;
      if ((ig === 'call' && c !== handle) || (pack && c && c !== handle)) {
        hidden = true;
        ord = 9000 + i;
      } else if (i === lead) ord = 0;
      else if (ig === 'call') ord = 1000 + i;
      else if (i === life) ord = 2000;
      else if (ig === 'info') ord = 3000 + i;
      else if (pack && !c) ord = 5000 + i;
      else if (pack) ord = 4000 + i;
      else if (c === handle) ord = 6000 + i;
      else ord = 7000 + i;
      el.hidden = hidden;
      el.style.order = ord;
    };
    this.slides.forEach(apply);
    this.thumbs?.forEach(apply);
    const first = this.visibleSlides()[0];
    this.slides.forEach((sl) => sl.classList.toggle('is-lead', sl === first));
    this.capGrid();
    // colour hook for sections lower on the page (no listener since the explain strip was removed;
    // kept for a future again-why colour follow)
    document.dispatchEvent(new CustomEvent('again:colour', { detail: { handle } }));
    if (this.track) {
      if (!this.isGrid()) this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.index = 0;
      this.markActive(this.visibleSlides());
    }
  }

  /* ---------------- size guide ---------------- */
  setupSizeGuide() {
    const dlg = this.querySelector('[data-size-guide]');
    if (!dlg) return;
    const open = () => {
      if (dlg.open) return;
      dlg.showModal();
      document.documentElement.classList.add('ag-lock');
    };
    this.querySelectorAll('[data-open-size-guide]').forEach((b) => b.addEventListener('click', open));
    document.addEventListener('click', (e) => {
      const b = e.target.closest?.('[data-open-size-guide]');
      if (b && !this.contains(b)) {
        e.preventDefault();
        open();
      }
    });
    dlg.addEventListener('close', () => document.documentElement.classList.remove('ag-lock'));
    dlg.querySelector('[data-close]')?.addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close();
    });
  }

  /* ---------------- sticky mobile ATC ---------------- */
  setupSticky() {
    const bar = this.querySelector('[data-sticky]');
    const atc = this.querySelector('[data-atc]');
    if (!bar || !atc || !('IntersectionObserver' in window)) return;
    let pastAtc = false;
    let nearFooter = false;
    const update = () => {
      const show = pastAtc && !nearFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('[data-sticky-btn]').tabIndex = show ? 0 : -1;
    };
    new IntersectionObserver(([e]) => {
      pastAtc = !e.isIntersecting && e.boundingClientRect.top < 0;
      update();
    }).observe(atc);
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver(([e]) => {
        nearFooter = e.isIntersecting;
        update();
      }).observe(footer);
    }
    bar.querySelector('[data-sticky-btn]').addEventListener('click', () => {
      if (!this.sizeChosen) this.promptSize();
      else this.add();
    });
  }

  /* ---------------- delivery pill (snippets/again-delivery-pill, port of the Jennifer "ships by" pill) ----------------
     ships by = today + processing business days; estimated delivery = + transit min … max. Weekends skipped. */
  renderDelivery() {
    const by = this.querySelector('[data-ship-by]');
    const eta = this.querySelector('[data-ship-eta]');
    if (!by || !eta) return;
    const addBiz = (d, n) => {
      const x = new Date(d);
      while (x.getDay() === 0 || x.getDay() === 6) x.setDate(x.getDate() + 1);
      let left = n;
      while (left > 0) {
        x.setDate(x.getDate() + 1);
        if (x.getDay() !== 0 && x.getDay() !== 6) left -= 1;
      }
      return x;
    };
    const p = Number(this.dataset.processing) || 0;
    const now = new Date();
    const s = addBiz(now, p);
    const a = addBiz(now, p + (Number(this.dataset.transitMin) || 0));
    const b = addBiz(now, p + (Number(this.dataset.transitMax) || 0));
    const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const md = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    by.textContent = p === 0 && s.toDateString() === now.toDateString() ? 'today' : day.format(s);
    eta.textContent = a.getMonth() === b.getMonth()
      ? `${md.format(a)}–${b.getDate()}`
      : `${md.format(a)} – ${md.format(b)}`;
  }
}

if (!customElements.get('again-product')) customElements.define('again-product', AgainProduct);
